import { Box, useTheme } from '@mui/material';
import CharacterCount from '@tiptap/extension-character-count';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { type Editor, EditorContent, type Extensions, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import DOMPurify from 'dompurify';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import RichTextToolbar from './RichTextToolbar';

interface RichTextEditorProps {
  value: string; // HTML
  onChange: (value: string) => void;
  editable?: boolean;
  stickyOffset?: number | string;
  onCreate?: (editor: Editor) => void;
  hideToolbar?: boolean;
  maxLength?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  editable = true,
  stickyOffset: _stickyOffset,
  onCreate,
  hideToolbar = false,
  maxLength,
}) => {
  const theme = useTheme();

  const extensions = useMemo(() => {
    const baseExtensions: Extensions = [
      StarterKit,
      // Link.configure({
      //     openOnClick: false,
      // }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      // Underline,
      Subscript,
      Superscript,
    ];

    if (maxLength) {
      baseExtensions.push(CharacterCount.configure({ limit: maxLength }));
    }

    return baseExtensions;
  }, [maxLength]);

  const editor = useEditor({
    extensions,
    content: DOMPurify.sanitize(value), // Initialize with prop value
    editable: editable,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    onCreate: ({ editor }) => {
      if (onCreate) {
        onCreate(editor);
      }
    },
  });

  useEffect(() => {
    // Handle external updates if necessary, but be careful
    // Only update if editor exists, value exists, and editor is NOT focused to avoid overwriting user input
    if (editor && value && !editor.isFocused) {
      // Optional: Compare current content to avoid unnecessary updates?
      // editor.commands.setContent(value);
      // Note: setContent might reset cursor.
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        border: 2,
        borderColor: (theme) => theme.palette.bidlogiq?.orange || 'orange',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'common.white',
        color: 'common.black',
        boxShadow: 1,
      }}
    >
      {!hideToolbar && <RichTextToolbar editor={editor} sticky={true} stickyOffset={0} />}

      <Box
        sx={{
          '& .ProseMirror': {
            minHeight: '150px',
            padding: 2,
            outline: 'none',
          },
          '& .ProseMirror p': { marginBottom: '1em' },
          '& .ProseMirror ul': { paddingLeft: '1.5em', listStyleType: 'disc' },
          '& .ProseMirror ol': { paddingLeft: '1.5em', listStyleType: 'decimal' },
          '& .ProseMirror blockquote': {
            borderLeft: '3px solid #ccc',
            paddingLeft: '1em',
            fontStyle: 'italic',
          },
          '& .ProseMirror h1, & .ProseMirror h2, & .ProseMirror h3, & .ProseMirror h4, & .ProseMirror h5':
            {
              fontWeight: 'bold',
              position: 'relative',
              paddingLeft: '2.5rem', // Space for the Hx marker
            },
          '& .ProseMirror h1': {
            fontSize: '1.8em',
            borderBottom: '2px solid #eee',
            pb: 1,
            mb: 2,
            '&::before': {
              content: '"H1"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#fff',
              backgroundColor: theme.palette.bidlogiq?.brick || '#D05C09',
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& .ProseMirror h2': {
            fontSize: '1.5em',
            mt: 3,
            mb: 1.5,
            '&::before': {
              content: '"H2"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#fff',
              backgroundColor: theme.palette.bidlogiq?.rust || '#812A16',
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& .ProseMirror h3': {
            fontSize: '1.25em',
            mt: 2,
            mb: 1,
            '&::before': {
              content: '"H3"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#000', // Black text for contrast on Camel
              backgroundColor: theme.palette.bidlogiq?.camel || '#d39951',
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& .ProseMirror h4': {
            fontSize: '1.1em',
            mt: 1.5,
            mb: 0.5,
            '&::before': {
              content: '"H4"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#000', // Black text for contrast on Orange
              backgroundColor: theme.palette.bidlogiq?.orange || '#FF6800',
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& .ProseMirror h5': {
            fontSize: '1em',
            mt: 1,
            mb: 0.5,
            '&::before': {
              content: '"H5"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#fff',
              backgroundColor: theme.palette.bidlogiq?.charcoal || '#231e21',
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& .ProseMirror table': {
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
            width: '100%',
            margin: '0',
            overflow: 'hidden',
            color: 'inherit',
          },
          '& .ProseMirror td, & .ProseMirror th': {
            minWidth: '1em',
            border: `2px solid ${theme.palette.divider}`,
            padding: '3px 5px',
            verticalAlign: 'top',
            boxSizing: 'border-box',
            position: 'relative',
          },
          '& .ProseMirror th': {
            fontWeight: 'bold',
            textAlign: 'left',
            backgroundColor: theme.palette.greyContrast,
            color: theme.palette.primary.contrastText,
          },
          '& .ProseMirror .selectedCell:after': {
            zIndex: 2,
            position: 'absolute',
            content: '""',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            background: 'rgba(200, 200, 255, 0.4)',
            pointerEvents: 'none',
          },
          '& .ProseMirror .column-resize-handle': {
            position: 'absolute',
            right: -2,
            top: 0,
            bottom: -2,
            width: 4,
            backgroundColor: '#adf',
            pointerEvents: 'none',
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
